import { useMemo } from 'react';

import { useCreateLegalClient as useCreateLegalClientApi } from 'api/requests';
import { ErrorCode } from 'api/types/error';
import { isFinanceProject, isGlobalProject } from 'config';
import { useServerErrorNotify } from 'hooks';
import { FormSubmit } from 'libs/form/Form';
import { useTranslation } from 'libs/i18n';
import { ROUTES, useNavigate } from 'libs/navigation';
import { useNotify } from 'libs/notify';

import {
  CreateLegalClientFormType,
  FormErrors,
} from '../forms/createLegalClientForm';

export const useCreateLegalClient = () => {
  const notify = useNotify();
  const { t } = useTranslation();
  const { navigate } = useNavigate();
  const isFinance = isFinanceProject();
  const isGlobal = isGlobalProject();

  const {
    createClient,
    error: submitError,
    loading: submitLoading,
  } = useCreateLegalClientApi();

  const formError = useMemo((): FormErrors | undefined => {
    if (submitError?.code === ErrorCode.USER_WITH_EMAIL_ALREADY_EXISTS) {
      return { representative: { email: submitError?.message } };
    }

    return undefined;
  }, [submitError?.code, submitError?.message]);

  const onSubmit: FormSubmit<CreateLegalClientFormType> = async (values) => {
    const client = await createClient({
      fullName: values.legal.fullName,
      personalAccountNumber: values.legal.personalAccountNumber,
      personalAccountOpeningDate: values.legal.personalAccountOpeningDate,
      companySignedClientServiceAgreementFileId_Global: isGlobal
        ? values.companySignedClientServiceAgreementFiles[0].id!
        : undefined,
      companySignedNoticeOfInvestmentAccountOpeningFileId:
        values.companySignedNoticeOfInvestmentAccountOpeningFiles[0].id!,
      serviceContractFileId_Finance: isFinance
        ? values.companySignedClientServiceAgreementFiles[0].id!
        : undefined,
      brightCode: values.legal.brightCode,
      representative: {
        email: values.representative.email!,
        firstName: values.representative.firstName!,
        lastName: values.representative.lastName!,
        middleName: values.representative.middleName,
        phoneNumber: values.representative.phoneNumber!,
        dateOfBirth: values.representative.dateOfBirth!,
        roleInCompany: {
          roleInCompany: values.representative.role.value,
          civilLawSubject: values.representative.civilLawSubject?.value,
          dateOfIssue: values.representative.dateOfIssue,
          dateOfExpiration: values.representative.dateOfExpiration ?? undefined,
        },
        companyFullName: values.representative.companyFullName ?? undefined,
      },
    });

    if (client) {
      notify.info(t('client.createLegalClient.successMessage'));
      navigate(ROUTES.clients.legalClients.legalClient.fullPath, {
        legalClientId: client.id,
      });
    }
  };

  useServerErrorNotify(submitError);

  return { onSubmit, formError, submitLoading };
};
